import {
  FC,
  FocusEvent,
  Fragment,
  KeyboardEvent
} from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import { KeyCode } from '@app/const';
import { Carousel } from '@app/form_components';
import { UserTypeSelector } from '@app/form_features';
import { useDesign } from '@app/hooks';
import { ChannelSettingSelector, LanguageSettingSelector } from '@app/page-components/shared';
import {
  formatHotkey,
  isDigitKey
} from '@app/utilities';
// This import cannot be shortened without creating circular dependencies.
import { modifierKey } from '@app/utilities/functions/environment/modifierKey';
import { runToolbarCommand } from './runToolbarCommand';
import { runToolbarDragPayload } from './runToolbarDragPayload';
import styles from './CanvasToolbar.module.scss';

// The zoom input holds at most "100" before the appended percent sign.
const maxZoomDigits = 3;

type CanvasToolbarButton = {
  /** Discriminates @see CanvasToolbarItem against @see CanvasToolbarBreak. */
  type?: 'button';
  icon: string;
  id: string;
  /** Chord parts for the tooltip, joined as [Ctrl+Z] or [⌘+Z]. */
  hotkey?: string[];
  isDragSource?: boolean;
  /** English fallback when `labelKey` is missing or unresolved. */
  label: string;
  /** Existing `window.l.toolbar` key; omit for historically hardcoded labels. */
  labelKey?: string;
};

type CanvasToolbarBreak = {
  type: 'break';
  id: string;
};

type CanvasToolbarItem = CanvasToolbarButton | CanvasToolbarBreak;

const canvasToolbarItems: CanvasToolbarItem[] = [
  {
    icon: 'check-square',
    id: 'icon-check-square',
    label: 'Validate application',
    labelKey: 'validate_application'
  },
  {
    icon: 'check-circle',
    id: 'icon-validate',
    label: 'Validate diagram',
    labelKey: 'validate_diagram'
  },
  {
    icon: 'download',
    id: 'icon-download',
    label: 'Download module',
    labelKey: 'download_module'
  },
  {
    icon: 'aperture',
    id: 'icon-capture',
    label: 'Download image',
    labelKey: 'download_image'
  },
  {
    icon: 'x-circle',
    id: 'icon-show-disabled',
    label: 'Show disabled items'
  },
  { type: 'break', id: 'break-after-show-disabled' },
  {
    icon: 'corner-up-left',
    id: 'icon-undo',
    hotkey: [modifierKey, 'Z'],
    label: 'Undo',
    labelKey: 'undo'
  },
  {
    icon: 'corner-up-right',
    id: 'icon-redo',
    hotkey: [modifierKey, 'Shift', 'Z'],
    label: 'Redo',
    labelKey: 'redo'
  },
  {
    icon: 'copy',
    id: 'icon-copy',
    hotkey: [modifierKey, 'C'],
    label: 'Copy',
    labelKey: 'copy'
  },
  {
    icon: 'clipboard',
    id: 'icon-paste',
    hotkey: [modifierKey, 'V'],
    label: 'Paste',
    labelKey: 'paste'
  },
  {
    icon: 'trash-2',
    id: 'icon-delete',
    hotkey: ['Delete'],
    label: 'Delete',
    labelKey: 'delete'
  },
  { type: 'break', id: 'break-after-delete' },
  {
    icon: 'message-square',
    id: 'icon-message-square',
    isDragSource: true,
    label: 'Create prompt',
    labelKey: 'create_prompt_drag'
  },
  {
    icon: 'mic',
    id: 'icon-mic',
    isDragSource: true,
    label: 'Create input',
    labelKey: 'create_input_drag'
  },
  {
    icon: 'cpu',
    id: 'icon-cpu',
    isDragSource: true,
    label: 'Create custom component',
    labelKey: 'create_custom_drag'
  },
  {
    icon: 'package',
    id: 'icon-package',
    isDragSource: true,
    label: 'Create module',
    labelKey: 'create_module_drag'
  },
  {
    icon: 'file-text',
    id: 'icon-note',
    isDragSource: true,
    label: 'Create note',
    labelKey: 'create_note_drag'
  },
  { type: 'break', id: 'break-after-note' },
  {
    icon: 'zoom-out',
    id: 'icon-zoom-out',
    hotkey: [modifierKey, '-'],
    label: 'Zoom out',
    labelKey: 'zoom_out'
  },
  {
    icon: 'zoom-in',
    id: 'icon-zoom-in',
    hotkey: [modifierKey, '='],
    label: 'Zoom in',
    labelKey: 'zoom_in'
  },
  {
    icon: 'eye',
    id: 'icon-toggle-disabled-elements',
    label: 'Toggle disabled elements'
  },
  {
    icon: 'crosshair',
    id: 'icon-center-canvas',
    label: 'Center canvas'
  }
];

function toolbarLabel(labelKey: string | undefined, fallback: string): string {
  if (!labelKey) {
    return fallback;
  }

  const toolbar = window.l?.toolbar as Record<string, string> | undefined;
  return toolbar?.[labelKey] || fallback;
}

function hidePropertyPanel(): void {
  window.apm?.postEventToCurrentView({ target: 'request-hide-property-panel' });
}

export const CanvasToolbar: FC = () => {
  const serialized = useDesign();

  const onZoomFocus = (event: FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    input.dataset.val = input.value;
    input.placeholder = input.value;
    input.value = '';
  };

  const onZoomBlur = (event: FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    let zoom = Number.parseInt(input.value, 10);

    if (Number.isNaN(zoom)) {
      zoom = Number.parseInt(input.dataset.val ?? '', 10);
    }

    window.apm?.postEventToCurrentView({
      target: 'request-zoom-set',
      param: { zoom }
    });
  };

  // `keypress` rather than `keydown` so editing keys still reach the input.
  const onZoomKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.code === KeyCode.enter) {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (!isDigitKey(event) || event.currentTarget.value.length >= maxZoomDigits) {
      event.preventDefault();
    }
  };

  return (
    <Carousel
      classNames={{
        carousel: styles.toolbar,
        track: styles.toolbarTrack
      }}
    >
      {canvasToolbarItems.map((item) => {
        if (item.type === 'break') {
          return (
            <div
              key={item.id}
              className={styles.toolbarBreak}
              role="separator"
              aria-orientation="vertical"
            />
          );
        }

        const label = toolbarLabel(item.labelKey, item.label);
        const title = (!item.hotkey) ? label : (
          `${label} ${formatHotkey(...item.hotkey)}`
        );
        return (
          <Fragment key={item.id}>
            <button
              id={item.id}
              type="button"
              className={styles.toolbarButton}
              draggable={item.isDragSource}
              aria-label={label}
              title={title}
              onClick={() => runToolbarCommand(item.id)}
              onDragStart={(!item.isDragSource) ? undefined : (
                () => runToolbarDragPayload(item.id)
              )}
            >
              <i className={`fe icon-${item.icon}`} />
            </button>
            {(item.id !== 'icon-zoom-out') ? null : (
              <input
                id="value-zoom"
                className={styles.zoomInput}
                type="text"
                size={4}
                defaultValue="100%"
                title={`Zoom ${formatHotkey('Enter')}`}
                onFocus={onZoomFocus}
                onBlur={onZoomBlur}
                onKeyPress={onZoomKeyPress}
              />
            )}
          </Fragment>
        );
      })}
      {(DesignerSetting.s.env === 'PROD') ? null : (
        <>
          <div
            className={styles.toolbarBreak}
            role="separator"
            aria-orientation="vertical"
          />
          <div className="toolbar-dev-tools">
            {(!serialized.omniChannel.isOmniChannel) ? null : (
              <div className="dropdown" onClick={hidePropertyPanel}>
                <ChannelSettingSelector />
              </div>
            )}
            <div className="dropdown" onClick={hidePropertyPanel}>
              <LanguageSettingSelector />
            </div>
            <div className="dropdown user_type" onClick={hidePropertyPanel}>
              <UserTypeSelector />
            </div>
          </div>
          <button
            id="author"
            type="button"
            className={styles.toolbarAuthor}
            title={window.l?.toolbar?.author}
          >
            {window.apm?.getDesignerAuthor?.()}
          </button>
        </>
      )}
    </Carousel>
  );
};
